import type { ComponentProps, Dispatch, ReactElement, SetStateAction } from "react"
import type { Option } from "#components/option"

import { useEffect, useState } from "react"

import { MultiStepView } from "#components/lib/layout/multiStepProgress/multiStepView"
import { twMerge } from "#lib/twMergeConfig"

/**
 * View, Step Indicator & Control implementation wired together
 */
export function MultiStepProgress<TControlAppearance extends object>({
  className,
  children,
  onSubmitStep,
  step,
  setStep,
  contentHeight,
  Controls,
  stepView,
  controlAppearance,
  initialHeight,
  onPreviousStep,
  formSubmitKey,
  shouldDisplayLoadingSpinner,
}: MultiStepProps<TControlAppearance>) {
  return (
    <div className={twMerge("-mx-5 flex h-full flex-col justify-between overflow-x-clip px-5", className)}>
      <MultiStepView {...stepView} currentStep={step} height={contentHeight} initialHeight={initialHeight}>
        {children}
      </MultiStepView>
      <Controls
        step={step}
        setStep={setStep}
        onSubmitStep={onSubmitStep}
        onPreviousStep={onPreviousStep}
        amountOfSteps={children.length}
        appearance={controlAppearance}
        formSubmitKey={formSubmitKey}
        shouldDisplayLoadingSpinner={shouldDisplayLoadingSpinner}
      />
    </div>
  )
}

type MultiStepPropsCommon = {
  /**
   * Current step - should come from useState
   * Example: const [step, setStep] = useState(1)
   */
  step: number

  /**
   * Function to change current step - should come from useState
   * Example: const [step, setStep] = useState(1)
   */
  setStep: Dispatch<SetStateAction<number>>

  /**
   * Return false for the given step to prevent the multistep form from proceeding to the next step
   */
  onSubmitStep?: (step: number) => boolean | Promise<boolean>

  /**
   * Gets run when the previous button gets hit
   */
  onPreviousStep?: (step: number) => void | Promise<void>

  /**
   * Return true to display a loading spinner when the current step is changing to the next step
   */
  shouldDisplayLoadingSpinner?: (step: number) => boolean | Promise<boolean>

  /**
   * Can be used to uniquely identify a form that is used with a multistepprogress - CAUTION! changes Control Implementation behaviour because it then waits for a form completion to re-enable controls again
   */
  formSubmitKey?: string
}

type MultiStepProps<TControlAppearance extends object> = MultiStepPropsCommon & {
  /**
   * Height to initially render, before current active view gets automatically calculated, can be used to prevent layout shifts
   */
  initialHeight?: number

  /**
   * Optional and gives the wrapper a fixed height - otherwise it automatically calcs the currently active view  height - necessary because the children are absolute and therefore the section container has height 0
   */
  contentHeight?: number

  /**
   * Generic Control implementation that can be used to control the multistep progress
   */
  Controls: React.FC<MultiStepControlProps<TControlAppearance>>

  /**
   * The appearance properties of the used Control implementation
   */
  controlAppearance: TControlAppearance

  /**
   * Properties for the Step Views that get rendered based on current step
   */
  stepView?: Omit<ComponentProps<typeof MultiStepView>, "currentStep" | "children" | "height">

  className?: string
  children: ReactElement<ComponentProps<typeof Option>>[]
}

export type MultiStepControlProps<TControlAppearance extends object> = MultiStepPropsCommon & {
  amountOfSteps: number
  appearance: TControlAppearance
}
